import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import { Checkbox } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';

import { PlainHtmlButton } from '../../common/plainHtmlButton/PlainHtmlButton';

interface ClientFilterItemProps {
  isChecked: boolean;
  isIndeterminate: boolean;
  children?: React.ReactNode;
  defaultCollapsed?: boolean;
  name: string;
  onClientSelected?: ({
    checked,
    name,
  }: {
    checked: boolean;
    name: string;
  }) => void;
  onOnlyClientSelected?: ({ name }: { name: string }) => void;
  onClientCollapsedChanged?: (name: string, expanded: boolean) => void;
  canViewVersions?: boolean;
}

export const ClientFilterItem = ({
  children,
  isChecked,
  isIndeterminate,
  name,
  onClientSelected,
  onOnlyClientSelected,
  defaultCollapsed = true,
  onClientCollapsedChanged,
  canViewVersions,
}: ClientFilterItemProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
    if (onClientCollapsedChanged) {
      onClientCollapsedChanged(name, !collapsed);
    }
  }, [collapsed, name, onClientCollapsedChanged]);

  const handleCheckboxClick = useCallback(
    (eventChecked: boolean) => {
      if (!onClientSelected) {
        return;
      }

      onClientSelected({ name, checked: eventChecked });
    },
    [name, onClientSelected],
  );

  const handleOnlyClick = useCallback(() => {
    if (!onOnlyClientSelected) return;
    onOnlyClientSelected({ name });
  }, [name, onOnlyClientSelected]);

  const isUnidentifiedClient = name === '';

  return (
    <div
      className={classnames(
        'group flex flex-col rounded px-2 py-[6px] hover:bg-btn-secondary-hover',
        {
          'bg-btn-secondary-selected': !collapsed,
        },
      )}
    >
      <div className="flex w-full items-center">
        <Checkbox
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          onChange={(e) =>
            handleCheckboxClick(isIndeterminate ? true : e.target.checked)
          }
          className={classnames(
            'flex max-w-[512px] grow items-center break-all text-base',
            {
              italic: isUnidentifiedClient,
            },
          )}
          title={name}
        >
          {isUnidentifiedClient ? 'Unidentified clients' : name}
        </Checkbox>
        <div className="invisible text-right group-hover:visible">
          <PlainHtmlButton
            className="ml-2 cursor-pointer pl-2 text-link hover:underline"
            data-testid="select-only-button"
            onClick={handleOnlyClick}
            onMouseDown={(e) => {
              // Without this the popover will close onMouseOut of this
              // element's parent since the popover loses focus. This looks to
              // be a bug in Chrome and Firefox with Chakra's popover when
              // clicking a hidden button.
              e.preventDefault();
            }}
          >
            only
          </PlainHtmlButton>
        </div>
        {canViewVersions && React.Children.count(children) > 0 && (
          <div
            className="invisible relative min-w-7 cursor-pointer rounded pl-2 pr-1 text-right leading-normal opacity-50 hover:opacity-100 group-hover:visible"
            onClick={toggleCollapsed}
          >
            {collapsed ? <IconChevronDown /> : <IconChevronUp />}
          </div>
        )}
      </div>
      {canViewVersions && (
        <div>
          <div className={classnames('pl-4', { hidden: collapsed })}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};
